'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import FooterColumn, { FooterLink } from './footerColumn/footerColumn';
import SocialLinks from './socialLinks/socialLinks';
import { LogoWhiteIcon } from '@/componentv2/icons';
import AppLink from '@/componentv2/AppLink/AppLink';
import './footer.scss';
import { productsNavLinks } from '@/componentv2/nav/productNavLinks';
import { DASHBOARD_URL } from '@/constants/routes';

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface FooterV2Props {
  sections?: FooterSection[];
  logoSrc?: string;
  logoAlt?: string;
}

function getDefaultSections(): FooterSection[] {
  return [
    {
      title: 'Products',
      links: productsNavLinks.map(({ label, href }) => ({ label, href })),
    },
    {
      title: 'About',
      links: [
        { label: 'Pricing', href: '/pricing' },
        { label: 'About Us', href: '/about' },
        {
          label: 'Social Impact',
          href: '/social-impact',
        },
        { label: 'Press Kit', href: '/media' },
        { label: 'Press Releases', href: '/news' },
        { label: 'Resources & Tips', href: '/resources' },
        { label: 'Blog', href: '/blog' },
        { label: 'Case Studies', href: '/case-studies' },
        { label: 'Video', href: '/videos' },
      ],
    },
    {
      title: 'Support',
      links: [
        {
          label: 'Help Center',
          href: 'https://support.signupgenius.com/hc/en-us',
        },
        {
          label: 'API Docs',
          href: 'https://developer.signupgenius.com/',
        },
        { label: 'Contact Us', href: '/help' },
        {
          label: 'Careers',
          href: '/careers',
        },
        {
          label: 'Product Updates',
          href: 'https://support.signupgenius.com/hc/en-us/categories/37113207959447-New-Features',
        },
      ],
    },
    {
      title: 'My Account',
      links: [
        {
          label: 'Dashboard',
          href: DASHBOARD_URL,
        },
        { label: 'Templates', href: '/templates' },
      ],
    },
  ];
}

const Footer: React.FC<FooterV2Props> = ({
  sections,
  logoSrc = '/n_images/SignUpGenius-Logo-FullColor.svg',
  logoAlt = 'SignUpGenius',
}) => {
  const [showCmsLabel, setShowCmsLabel] = useState(false);
  const resolvedSections = sections ?? getDefaultSections();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
      const isRocksDomain = hostname.endsWith('.signupgenius.rocks');
      setShowCmsLabel(isLocalhost || isRocksDomain);
    }
  }, []);

  return (
    <footer className="footer">
      <div className="footer__container">
        <div className="footer__main">
          <div className="footer__columns">
            {resolvedSections.map((section, index) => (
              <FooterColumn
                key={index}
                title={section.title}
                links={section.links}
              />
            ))}
          </div>
          <div className="footer__brand">
            <div className="footer__logo">
              <Image
                src={logoSrc}
                alt={logoAlt}
                width={200}
                height={40}
                sizes="(max-width: 767px) 50vw, (max-width: 1200px) 30vw, 20vw"
              />
            </div>
            <SocialLinks className="footer__brand-social" />
          </div>
        </div>

        <div className="footer__mobile-social">
          <SocialLinks />
        </div>
      </div>

      <div className="footer_bottom">
        <div className="footer__container">
          <div className="footer__bottom">
            <div className="footer__mobile-logo">
              <LogoWhiteIcon width={163} height={37} />
            </div>
            <div className="footer__bottom-content">
              <p className="footer__copyright">
                ©{new Date().getFullYear()} SignUpGenius Inc. All Rights
                Reserved.
              </p>
              <div className="footer__legal">
                <AppLink
                  className="footer__legal-link"
                  href="/privacy"
                  intent="navigation"
                  aria-label="Privacy Policy"
                >
                  Privacy Policy
                </AppLink>
                <AppLink
                  className="footer__legal-link"
                  href="/terms-of-service"
                  intent="navigation"
                  aria-label="Terms of Service"
                >
                  Terms of Service
                </AppLink>
                <a
                  className="change-consent pmLink footer__legal-link"
                  aria-label="Do Not Sell My Information"
                  style={{ visibility: 'hidden', cursor: 'pointer' }}
                >
                  &nbsp;
                </a>
                <a
                  className="change-consent gdpr-pmLink footer__legal-link"
                  aria-label="Privacy Preferences"
                  style={{ visibility: 'hidden', cursor: 'pointer' }}
                >
                  &nbsp;
                </a>
              </div>
            </div>
            {showCmsLabel && <span className="footer__cms-label">SUG-CMS</span>}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
